'use client';
import React, { useEffect } from 'react';
import { X, Download, FileText } from 'lucide-react';
import { formatDate, formatFileSize } from '@/lib/types';
import { toast } from 'sonner';

interface PreviewModalProps {
  doc: {
    title: string;
    file_url?: string;
    original_filename?: string;
    file_size?: number;
    updated_at?: string;
    slug?: string;
  };
  onClose: () => void;
}

export default function PreviewModal({ doc, onClose }: PreviewModalProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.document.addEventListener('keydown', handleKey);
    return () => window.document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleDownload = () => {
    if (doc.file_url) {
      const link = window.document.createElement('a');
      link.href = doc.file_url;
      link.download = doc.original_filename || `${doc.slug || 'document'}.pdf`;
      link.click();
      toast.success(`Downloading ${doc.title}`);
    } else {
      toast.info('No file available', { description: 'Upload a PDF via the admin dashboard.' });
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-foreground/50 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={`Preview: ${doc.title}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full sm:max-w-2xl lg:max-w-3xl bg-card rounded-t-2xl sm:rounded-2xl shadow-modal animate-slide-up flex flex-col max-h-[90vh] sm:max-h-[85vh]">
        {/* Modal header */}
        <div className="flex items-start justify-between gap-3 p-4 sm:p-5 border-b border-border flex-shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <FileText size={18} className="text-primary" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-foreground leading-tight line-clamp-2">
                {doc.title}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                PDF{doc.file_size ? ` • ${formatFileSize(doc.file_size)}` : ''}{doc.updated_at ? ` • Updated ${formatDate(doc.updated_at)}` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close preview"
            className="btn-ghost p-2 flex-shrink-0 -mt-1 -mr-1"
          >
            <X size={18} />
          </button>
        </div>

        {/* Preview area */}
        <div className="flex-1 overflow-hidden bg-muted flex items-center justify-center p-6">
          {doc.file_url ? (
            <iframe
              src={doc.file_url}
              title={`Preview: ${doc.title}`}
              className="w-full h-full rounded-lg border border-border"
              style={{ minHeight: '400px' }}
            />
          ) : (
            <div className="text-center max-w-sm">
              <div className="w-20 h-24 bg-card border-2 border-dashed border-border rounded-xl flex items-center justify-center mx-auto mb-4">
                <FileText size={32} className="text-muted-foreground" aria-hidden="true" />
              </div>
              <p className="text-sm font-semibold text-foreground mb-1">{doc.original_filename || doc.title}</p>
              <p className="text-xs text-muted-foreground mb-4">
                This is a sample entry. Upload a real PDF via the admin dashboard to enable preview.
              </p>
            </div>
          )}
        </div>

        {/* Modal footer */}
        <div className="flex gap-3 p-4 sm:p-5 border-t border-border flex-shrink-0">
          <button onClick={onClose} className="btn-secondary flex-1 sm:flex-none">Close</button>
          <button onClick={handleDownload} className="btn-accent flex-1 sm:flex-none gap-2">
            <Download size={14} aria-hidden="true" />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}